/**
 * Reducer for BuyTokensModal component state management.
 */

import type { DiscountPreview } from '../promo';

// ============================================================================
// Types (re-exported for use in the component)
// ============================================================================

export interface TokenPackage {
  id: string;
  name: string;
  tokens: number;
  price_usd: number;
  price_per_1000: number;
  discount_percent: number;
  savings_usd: number;
  popular: boolean;
  available?: boolean;
}

export interface PaymentDetails {
  payment_address: string;
  payment_amount: string;
  payment_currency: string;
  chain: string;
  package_name: string;
  package_tokens: number;
  price_usd: number;
}

export interface CurrencyOption {
  code: string;
  chain?: string;
}

// ============================================================================
// State
// ============================================================================

export interface BuyTokensState {
  packages: TokenPackage[];
  supportedCurrencies: CurrencyOption[];
  loading: boolean;
  purchasing: string | null;
  error: string | null;
  selectedCurrency: string;
  paymentDetails: PaymentDetails | null;
  copied: 'address' | 'amount' | null;
  promoCode: string | null;
  promoDiscount: DiscountPreview | null;
}

export const initialState: BuyTokensState = {
  packages: [],
  supportedCurrencies: [],
  loading: true,
  purchasing: null,
  error: null,
  selectedCurrency: 'ETH',
  paymentDetails: null,
  copied: null,
  promoCode: null,
  promoDiscount: null,
};

// ============================================================================
// Actions
// ============================================================================

export type BuyTokensAction =
  | { type: 'SET_PACKAGES'; payload: TokenPackage[] }
  | { type: 'SET_SUPPORTED_CURRENCIES'; payload: CurrencyOption[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_PURCHASING'; payload: string | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SELECTED_CURRENCY'; payload: string }
  | { type: 'SET_PAYMENT_DETAILS'; payload: PaymentDetails | null }
  | { type: 'SET_COPIED'; payload: 'address' | 'amount' | null }
  | { type: 'SET_PROMO_CODE'; payload: string | null }
  | { type: 'SET_PROMO_DISCOUNT'; payload: DiscountPreview | null }
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { packages: TokenPackage[]; currencies: CurrencyOption[] } }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'BACK_TO_PACKAGES' }
  | { type: 'MODAL_OPENED' };

// ============================================================================
// Reducer
// ============================================================================

export function buyTokensReducer(state: BuyTokensState, action: BuyTokensAction): BuyTokensState {
  switch (action.type) {
    case 'SET_PACKAGES':
      return { ...state, packages: action.payload };

    case 'SET_SUPPORTED_CURRENCIES':
      return { ...state, supportedCurrencies: action.payload };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_PURCHASING':
      return { ...state, purchasing: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_SELECTED_CURRENCY':
      return { ...state, selectedCurrency: action.payload };

    case 'SET_PAYMENT_DETAILS':
      return { ...state, paymentDetails: action.payload };

    case 'SET_COPIED':
      return { ...state, copied: action.payload };

    case 'SET_PROMO_CODE':
      return { ...state, promoCode: action.payload };

    case 'SET_PROMO_DISCOUNT':
      return { ...state, promoDiscount: action.payload };

    case 'FETCH_START':
      return { ...state, loading: true, error: null };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        packages: action.payload.packages,
        supportedCurrencies: action.payload.currencies,
      };

    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };

    case 'BACK_TO_PACKAGES':
      return { ...state, paymentDetails: null, error: null };

    case 'MODAL_OPENED':
      return { ...state, paymentDetails: null, promoCode: null, promoDiscount: null };

    default:
      return state;
  }
}
